"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObjectEditor = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../../../theme");
const react_1 = require("react");
const compliance_pool_types_1 = require("../../compliance-pool-types");
const common_styled_components_1 = require("../../../common-styled-components");
const TextInput_1 = require("../../../generic/TextInput");
const NumberInput_1 = require("../../../generic/NumberInput");
const useFetchAll_1 = __importDefault(require("../../../../hooks/useFetchAll"));
const apiUtils_1 = require("../../../../util/apiUtils");
const optionUtil_1 = require("../../../../util/compliance/optionUtil");
const react_select_1 = __importDefault(require("react-select"));
const lodash_1 = require("lodash");
const defaultPoolByYearValue = {
    pool_id: null,
    pool_name: null,
    forecast_source: 1,
};
const List = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(0)};
  margin-top: ${theme_1.theme.spacing(3)};
`;
const SelectorColumn = styled_components_1.default.div `
  display: flex;
  flex-direction: column;
  gap: ${theme_1.theme.spacing(1)};
  flex: 1;
`;
const SelectorLabel = styled_components_1.default.span `
  font-size: 12px;
  font-weight: 600;
`;
const ObjectEditor = ({ title, initialValue, editorType, metadata, onChange, }) => {
    const [value, setValue] = (0, react_1.useState)(initialValue);
    const [overviewByYear, _setOverviewUrl, setOverviewBody, setOverviewYears] = (0, useFetchAll_1.default)("admin/overview.json", {});
    const poolYears = (0, react_1.useMemo)(() => editorType === "PoolsByYear"
        ? Object.keys(value)
            .map((yearKey) => Number(yearKey))
            .filter((year) => !isNaN(year))
        : [], [editorType, value]);
    (0, react_1.useEffect)(() => {
        if (editorType !== "PoolsByYear") {
            return;
        }
        setOverviewBody({});
        setOverviewYears({
            key: "pool_year",
            variations: poolYears.map((year) => String(year)),
        });
    }, [editorType, poolYears, setOverviewBody, setOverviewYears]);
    const emptyPool = (0, optionUtil_1.getEmptyOption)("pool");
    const poolOptionsByYear = (0, react_1.useMemo)(() => poolYears.reduce((acc, year, index) => {
        const yearOptions = overviewByYear.status === apiUtils_1.LoaderStatus.Success
            ? (overviewByYear.data[index]?.pools ?? []).map((pool) => ({
                label: pool.name,
                value: pool.id,
            }))
            : [];
        acc[year] = [emptyPool, ...yearOptions];
        return acc;
    }, {}), [poolYears, overviewByYear, emptyPool]);
    (0, react_1.useEffect)(() => {
        if (!(0, lodash_1.isEqual)(value, initialValue)) {
            onChange(value);
        }
    }, [value, onChange]);
    const editKey = (0, react_1.useCallback)((oldKey, newKey) => {
        setValue((prevValue) => {
            const updatedValue = { ...prevValue };
            const valueForKey = updatedValue[oldKey];
            delete updatedValue[oldKey];
            updatedValue[newKey] = valueForKey;
            return updatedValue;
        });
    }, []);
    const editYearKey = (0, react_1.useCallback)((oldYearKey, newYear) => {
        setValue((prevValue) => {
            const updated = { ...prevValue };
            const oldYear = Number(oldYearKey);
            const entry = updated[oldYear];
            delete updated[oldYear];
            updated[Number(newYear)] = entry ?? defaultPoolByYearValue;
            return updated;
        });
    }, []);
    const editValue = (0, react_1.useCallback)((key, newValue) => {
        setValue((prevValue) => {
            const updatedValue = { ...prevValue };
            updatedValue[key] = newValue;
            return updatedValue;
        });
    }, []);
    const editPoolForYear = (0, react_1.useCallback)((yearKey, poolId) => {
        setValue((prevValue) => {
            const updated = { ...prevValue };
            const year = Number(yearKey);
            const yearPoolOptions = poolOptionsByYear[year] ?? [emptyPool];
            const selected = yearPoolOptions.find((o) => o.value === poolId) ?? emptyPool;
            const current = updated[year] ?? defaultPoolByYearValue;
            updated[year] = {
                ...current,
                pool_id: selected.value,
                pool_name: selected.value === null ? null : selected.label,
            };
            return updated;
        });
    }, [poolOptionsByYear, emptyPool]);
    const editForecastSourceForYear = (0, react_1.useCallback)((yearKey, forecastSource) => {
        setValue((prevValue) => {
            const updated = { ...prevValue };
            const year = Number(yearKey);
            const current = updated[year] ?? defaultPoolByYearValue;
            updated[year] = {
                ...current,
                forecast_source: forecastSource,
            };
            return updated;
        });
    }, []);
    const remove = (0, react_1.useCallback)((key) => {
        setValue((prevValues) => {
            const updatedValues = (0, lodash_1.pickBy)(prevValues, (_v, k) => k !== key);
            return updatedValues;
        });
    }, []);
    const addNew = (0, react_1.useCallback)(() => {
        setValue((prevValue) => {
            if (editorType === "string") {
                const updatedValue = { ...prevValue };
                updatedValue[""] = "";
                return updatedValue;
            }
            const updatedPools = { ...prevValue };
            const years = Object.keys(updatedPools)
                .map((y) => Number(y))
                .filter((n) => !isNaN(n));
            const nextYear = years.length
                ? Math.max(...years) + 1
                : new Date().getFullYear();
            updatedPools[nextYear] = defaultPoolByYearValue;
            return updatedPools;
        });
    }, [editorType]);
    return ((0, jsx_runtime_1.jsxs)(List, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { children: title }), Object.keys(value).map((key, index) => {
                if (editorType === "string") {
                    const typedValue = value;
                    return ((0, jsx_runtime_1.jsxs)(common_styled_components_1.SeparatedRow, { children: [(0, jsx_runtime_1.jsx)(TextInput_1.TextInput, { value: key, onChange: (newKey) => editKey(key, newKey), placeholder: "Key" }), (0, jsx_runtime_1.jsx)(TextInput_1.TextInput, { value: typedValue[key], onChange: (newValue) => editValue(key, newValue), placeholder: "Value" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { onClick: () => remove(key), children: "Remove" })] }, index));
                }
                // PoolsByYear editor: year + pool selector per row
                const poolsByYear = value;
                const year = Number(key);
                const currentPoolId = poolsByYear[year]?.pool_id ?? null;
                const currentForecastSource = poolsByYear[year]?.forecast_source ??
                    defaultPoolByYearValue.forecast_source;
                const poolOptions = poolOptionsByYear[year] ?? [emptyPool];
                return ((0, jsx_runtime_1.jsxs)(common_styled_components_1.SeparatedRow, { children: [(0, jsx_runtime_1.jsxs)(SelectorColumn, { children: [(0, jsx_runtime_1.jsx)(SelectorLabel, { children: "Year" }), (0, jsx_runtime_1.jsx)(NumberInput_1.NumberInput, { value: year, onChange: (newYear) => editYearKey(key, newYear ?? year) })] }), (0, jsx_runtime_1.jsxs)(SelectorColumn, { children: [(0, jsx_runtime_1.jsx)(SelectorLabel, { children: "Pool" }), (0, jsx_runtime_1.jsx)(react_select_1.default, { options: poolOptions, onChange: (newValue) => editPoolForYear(key, newValue?.value ?? null), value: poolOptions.find((o) => o.value === currentPoolId) ??
                                        emptyPool, isOptionSelected: (option) => option.value === currentPoolId })] }), (0, jsx_runtime_1.jsxs)(SelectorColumn, { children: [(0, jsx_runtime_1.jsx)(SelectorLabel, { children: "Forecast source" }), (0, jsx_runtime_1.jsx)(react_select_1.default, { options: compliance_pool_types_1.forecastSourceOptions, onChange: (newValue) => editForecastSourceForYear(key, newValue?.value ?? 1), value: compliance_pool_types_1.forecastSourceOptions.find((option) => option.value === currentForecastSource) ?? compliance_pool_types_1.forecastSourceOptions[0], isOptionSelected: (option) => option.value === currentForecastSource })] }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { onClick: () => remove(key), children: "Remove" })] }, index));
            }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SecondaryButton, { onClick: addNew, children: "+ Add" })] }));
};
exports.ObjectEditor = ObjectEditor;
